import { MergeSuggestionDetailsDrawer } from '@modules/taxonomy/components/MergeSuggestionDetailsDrawer';
import {
  useActiveMergeSuggestionsState,
  useFetchRelatedReasons,
} from '@modules/taxonomy/hooks';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { ActiveMergeSuggestion } from '@modules/taxonomy/store/types';
import {
  Box,
  Button,
  CircularProgress,
  DialogProps,
  Stack,
  Typography,
} from '@mui/material';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { CreateOrUpdateLabelSuccessModal } from '../../CreateOrUpdateLabelSuccessModal';
import { trackCreateNewReasonEvents } from '../helpers';

interface CreateNewReasonSuccessModalProps
  extends Omit<DialogProps, 'onClose'> {
  onClose: () => void;
}

export const CreateNewReasonSuccessModal = ({
  open,
  onClose,
}: CreateNewReasonSuccessModalProps) => {
  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);

  const { reasonID, reasonCategory, reasonName } = useTaxonomyStore(
    ({ createReasonV3State: { reason } }) => ({
      reasonID: reason?.id,
      reasonName: reason?.name,
      reasonCategory: reason?.category,
    }),
  );

  const { data: relatedReasons, loading: isLoadingMergeSuggestions } =
    useFetchRelatedReasons(reasonID ? [reasonID] : []);

  const {
    activeMergeSuggestion,
    setActiveMergeSuggestion,
    onUpdateSuggestedName,
    onUpdateCategory,
    onAddSuggestion,
    onRemoveSuggestion,
  } = useActiveMergeSuggestionsState();

  if (!reasonID || !reasonName) {
    return null;
  }

  const openMergeReasonsDrawer = () => {
    const mergeSuggestion: ActiveMergeSuggestion = {
      id: reasonID,
      suggestedName: reasonName,
      category: reasonCategory,
      suggestedLabelsToMerge: [
        {
          id: reasonID,
          label: reasonName,
          category: reasonCategory,
        },
      ],
    };

    // Close the modal and open the drawer
    onClose?.();
    setActiveMergeSuggestion(mergeSuggestion);
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Review Merge Suggestions CTA Clicked',
    });
  };

  const closeDrawer = () => {
    setActiveMergeSuggestion(undefined);
    dispatch({
      type: TaxonomyActionType.RESET_CREATE_REASONV3_STATE,
    });
  };

  const handleModalClose = () => {
    onClose();
    dispatch({
      type: TaxonomyActionType.RESET_CREATE_REASONV3_STATE,
    });
  };

  const renderMergeSuggestionsContent = () => {
    const hasMergeSuggestions = relatedReasons.length > 0;

    if (isLoadingMergeSuggestions) {
      return (
        <Box borderRadius={1}>
          <Stack
            px={2.5}
            py={2}
            spacing={2}
            justifyContent="center"
            direction="row"
          >
            <CircularProgress size={18} />
            <Typography variant="body2">
              👀 Searching for similar Reasons to merge
            </Typography>
          </Stack>
        </Box>
      );
    }

    if (!hasMergeSuggestions) {
      return (
        <Box borderRadius={1} px={2} py={2}>
          <Typography variant="body2" textAlign="center">
            🪄 You've crafted something truly unique here! There's nothing else
            quite like it within the Taxonomy to merge with!
          </Typography>
        </Box>
      );
    }

    return (
      <Box borderRadius={1}>
        <Stack px={2.5} py={2} spacing={2.5} alignItems="center">
          <Typography variant="body2">
            <Typography variant="body2" color="text.secondary">
              We also found some similar Reasons that can be merged into{' '}
            </Typography>
            <Typography component="span" variant="body2">
              {reasonName}
            </Typography>
          </Typography>
          <Box px={2}>
            <Button variant="outlined" onClick={openMergeReasonsDrawer}>
              Review Merge Suggestions
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <CreateOrUpdateLabelSuccessModal
        open={open}
        action="create"
        label={{ name: reasonName, type: FeedbackLabelType.Reason }}
        footerNode={renderMergeSuggestionsContent()}
        onClose={handleModalClose}
      />
      {activeMergeSuggestion && (
        <MergeSuggestionDetailsDrawer
          activeMergeSuggestion={activeMergeSuggestion}
          closeDrawer={closeDrawer}
          onUpdateSuggestedName={onUpdateSuggestedName}
          onUpdateCategory={onUpdateCategory}
          onAddSuggestion={onAddSuggestion}
          onRemoveSuggestion={onRemoveSuggestion}
          drawerSource="Create Reason Success Modal"
          title="Review Merge Suggestions"
        />
      )}
    </>
  );
};
